import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, distinctUntilChanged, forkJoin, Subject, takeUntil } from 'rxjs';

import { LayoutComponent } from '../../../shared/layouts/layout/layout.component';
import { Media } from '../../models/tickets/media.model';
import { PlagueCategory } from '../../models/tickets/plague-category.model';
import { TicketType } from '../../models/tickets/ticket-type.model';
import { Status, Ticket } from '../../models/tickets/ticket.model';
import { NestingArea, Plague, TicketsService } from '../../services/tickets/tickets.service';

@Component({
  selector: 'app-tracings',
  standalone: true,
  imports: [
    AccordionModule,
    AutoCompleteModule,
    ButtonModule,
    CalendarModule,
    CommonModule,
    DialogModule,
    DropdownModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    InputTextareaModule,
    LayoutComponent,
    MultiSelectModule,
    ReactiveFormsModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    PaginatorModule,
  ],
  providers: [MessageService],
  templateUrl: './tracings.component.html',
  styleUrl: './tracings.component.scss'
})
export class TracingsComponent implements OnInit, OnDestroy {
  public tracingForm: FormGroup;
  private _ticketsService = inject(TicketsService);

  public tickets: Ticket[] = [];
  public loading: boolean = false;
  public isModalVisible: boolean = false;

  public ticketsTypes: TicketType[] = [];
  public mediaOptions: Media[] = [];
  public nestingAreas: NestingArea[] = [];
  public plagueCategories: PlagueCategory[] = [];
  public plagues: Plague[] = [];
  public statuses: Status[] = [];

  public dataForm!: FormGroup;
  private filterSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  public activeView: 'table' | 'kanban' = 'table';
  public kanbanColumns: any[] = [];
  public page_size: number = 10;
  public draggedTicket: any = null;
  public dragOverColumnId: string | null = null;

  public filteredTicketTypes: TicketType[] = [];
  public filteredStatuses: Status[] = [];

  constructor(
    private _messageService: MessageService,
    private _formBuilder: FormBuilder
  ) {
    this.tracingForm = this._formBuilder.group({
      comments: ['', [Validators.required]],
      ticketType: ['', [Validators.required]],
      media: ['', [Validators.required]],
      nestingArea: [[], [Validators.required]],
      plagueCategory: [[], [Validators.required]],
      plague: [[], [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.activeView = (localStorage.getItem('activeViewTickets') as 'table' | 'kanban') || 'table';
    this.page_size = Number(localStorage.getItem('page_sizeTickets')) || 10;

    this.loadDataForm();
    this.loadData();

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadTickets();
    }

    this.filterSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.activeView === 'kanban') {
        this.loadDataKanban();
      } else {
        this.loadTickets();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFilterInput(event: any, filter: string) {
    const inputElement = event.target as HTMLInputElement;
    this.dataForm.value[filter] = inputElement.value;

    this.filterSubject.next(inputElement.value);
  }

  resetFilters() {
    this.loadDataForm();

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadTickets();
    }
  }

  loadDataForm(): void {
    this.dataForm = this._formBuilder.group({
      totalRecords: 0,
      page: 1,
      page_size: this.page_size,
      firstPage: 0,
      search: '',
      status: '',
      ticket_type: '',
    });
  }

  loadTickets(): void {
    this.loading = true;

    const filters = {
      ...this.dataForm.value,
      status: this.dataForm.value.status instanceof Object ? this.dataForm.value.status.id : this.dataForm.value.status,
      ticket_type: this.dataForm.value.ticket_type instanceof Object ? this.dataForm.value.ticket_type.id : this.dataForm.value.ticket_type,
    };

    this._ticketsService.getTickets(filters).subscribe((data: any) => {
      this.tickets = data.items;

      this.dataForm.value.totalRecords = data.total;
      this.dataForm.value.page = data.page;
      this.dataForm.value.page_size = data.page_size;

      this.loading = false;
    });
  }

  loadData(): void {
    forkJoin({
      ticketTypes: this._ticketsService.getTicketTypes(),
      media: this._ticketsService.getMedia(),
      nestingAreas: this._ticketsService.getNestingAreas(),
      plagueCategories: this._ticketsService.getPlagueCategories(),
      plagues: this._ticketsService.getPlagues(),
      statuses: this._ticketsService.getTicketsStatuses(),
    }).subscribe({
      next: (results: any) => {
        this.ticketsTypes = results.ticketTypes.items;
        this.mediaOptions = results.media.items;
        this.nestingAreas = results.nestingAreas.items;
        this.plagueCategories = results.plagueCategories.items;
        this.plagues = results.plagues.items;
        this.statuses = results.statuses;
      },
      error: (error: any) => {
        console.error('Error al cargar los datos:', error);
      }
    });
  }

  filterTickets(event: any) {
    const query = event
      ? event.query.toLowerCase()
      : '';

    this._ticketsService.getTicketTypes(query).subscribe((data: any) => {
      this.filteredTicketTypes = data.items;
    });
  }

  filterStatus(event: any) {
    const query = event
      ? event.query.toLowerCase()
      : '';

    this._ticketsService.getTicketsStatuses(query).subscribe((data: any) => {
      this.filteredStatuses = data;
    });
  }

  filterPlagueCategory(event: any) {
    const query = event
      ? event.query.toLowerCase()
      : '';

    this._ticketsService.getPlagueCategories(query).subscribe((data: any) => {
      this.plagueCategories = data.items;
    });
  }

  filterMedia(event: any) {
    const query = event
      ? event.query.toLowerCase()
      : '';

    this._ticketsService.getMedia(query).subscribe((data: any) => {
      this.mediaOptions = data.items;
    });
  }

  filterNestingArea(event: any) {
    const query = event
      ? event.query.toLowerCase()
      : '';

    this._ticketsService.getNestingAreas(query).subscribe((data: any) => {
      this.nestingAreas = data.items;
    });
  }

  filterPlague(event: any) {
    const query = event
      ? event.query.toLowerCase()
      : '';

    this._ticketsService.getPlagues(query).subscribe((data: any) => {
      this.plagues = data.items;
    });
  }

  handleSave(event: Event): void {
    event.preventDefault();

    const today = new Date();
    const todayFormatted = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;

    if (this.tracingForm.valid) {
      const nestingAreas = Array.isArray(this.tracingForm.value.nestingArea)
        ? this.tracingForm.value.nestingArea.map((area: any) => area.id)
        : [this.tracingForm.value.nestingArea.id];

      const plagues = Array.isArray(this.tracingForm.value.plague)
        ? this.tracingForm.value.plague.map((item: any) => item.id)
        : [this.tracingForm.value.plague.id];

      this._ticketsService.createTicket(
        this.tracingForm.value.comments,
        this.tracingForm.value.ticketType.id,
        this.tracingForm.value.media.id,
        nestingAreas,
        this.tracingForm.value.plagueCategory.map((category: any) => category.id),
        plagues,
        todayFormatted
      ).subscribe({
        next: (data: Ticket) => {
          this._messageService.add({
            severity: 'success',
            summary: '¡Éxito!',
            detail: '¡El ticket se ha creado correctamente!'
          });

          this.loadDataForm();
          this.loadTickets();

          this.isModalVisible = false;
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Hubo un problema al crear el ticket. Por favor, inténtelo de nuevo.'
          });

          console.error('Error al crear el ticket:', error);
        }
      });

      this.isModalVisible = false;
    } else {
      this.tracingForm.markAllAsTouched();
    }
  }

  hasErrors(field: string, typeError: string) {
    return this.tracingForm.get(field)?.hasError(typeError) && this.tracingForm.get(field)?.touched;
  }

  pageChange(event: any) {
    const newPage = event.first / event.rows;

    this.dataForm.value.firstPage = event.first;
    this.dataForm.value.page = newPage + 1;

    this.loadTickets();
  }

  loadDataKanban(): void {
    this.loading = true;

    this._ticketsService.getTicketsStatuses().subscribe({
      next: (statusRes: any) => {
        const statusArray = statusRes.items || statusRes;
        this.kanbanColumns = statusArray.map((status: any) => ({
          statusId: status.id,
          title: status.name,
          color: status.color || '#4459a4',
          tickets: [],
          loading: false,
          currentPage: 1,
          totalPages: 1,
          total: 0
        }));

        this.kanbanColumns.forEach(column => {
          this.loadKanbanColumnData(column);
        });

        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('Error loading statuses:', error);
      }
    });
  }

  loadKanbanColumnData(column: any, page: number = 1): void {
    column.loading = true;

    const params: any = {
      page: page,
      page_size: this.page_size,
      status: column.statusId,
      search: this.dataForm.value.search || '',
      ticket_type: this.dataForm.value.ticket_type instanceof Object ? this.dataForm.value.ticket_type.id : this.dataForm.value.ticket_type,
    };

    this._ticketsService.getTickets(params).subscribe({
      next: (response: any) => {
        if (page === 1) {
          column.tickets = response.items;
        } else {
          column.tickets = [...column.tickets, ...response.items];
        }

        column.currentPage = response.page;
        column.totalPages = Math.ceil(response.total / this.page_size);
        column.total = response.total;
        column.loading = false;
      },
      error: (error) => {
        column.loading = false;
        console.error('Error loading column data:', error);
      }
    });
  }

  loadMoreKanbanItems(column: any): void {
    if (column.currentPage < column.totalPages) {
      this.loadKanbanColumnData(column, column.currentPage + 1);
    }
  }

  toggleView(view: 'table' | 'kanban') {
    this.activeView = view;
    localStorage.setItem('activeViewTickets', this.activeView);

    if (view === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadTickets();
    }
  }

  onDragStart(event: DragEvent, ticket: any): void {
    this.draggedTicket = ticket;
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragEnd(event: DragEvent): void {
    this.draggedTicket = null;
  }

  onDrop(event: DragEvent, targetStatusId: string): void {
    event.preventDefault();
    this.dragOverColumnId = null;

    if (this.draggedTicket && this.draggedTicket.status.id !== targetStatusId) {
      this.updateTicketStatus(this.draggedTicket, targetStatusId);
    }
  }

  onDragOver(event: DragEvent, columnId: string): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.dragOverColumnId = columnId;
  }

  onDragLeave(event: DragEvent): void {
    this.dragOverColumnId = null;
  }

  updateTicketStatus(ticket: any, newStatusId: string): void {
    this.loading = true;

    const data = {
      status: newStatusId
    };

    this._ticketsService.patchTicket(data, ticket.id).subscribe({
      next: (res) => {
        this._messageService.add({
          severity: 'success',
          summary: '¡Éxito!',
          detail: 'El estado del ticket se ha actualizado correctamente'
        });

        if (this.activeView === 'kanban') {
          this.loadDataKanban();
        } else {
          this.loadTickets();
        }

        this.loading = false;
      },
      error: (err) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el estado del ticket'
        });
        this.loading = false;
      }
    });
  }

  selectField(event: any, field: string) {
    const value = event ? (event.value || event) : '';

    this.dataForm.patchValue({
      [field]: value
    });

    if (this.activeView === 'kanban') {
      this.loadDataKanban();
    } else {
      this.loadTickets();
    }
  }

  getStatusClass(status: any): string {
    const statusMap: any = {
      'ticket-created': 'status-blue',
      'ticket-cancelled': 'status-gray',
      'ticket-closed': 'status-green',
      'ticket-unauthorized': 'status-red',
    };

    return statusMap[status.key_string] || 'status-yellow';
  }
}
